import { ArrowBack } from '@mui/icons-material';
import {
  type LinkPersonToProfileInfoReqBody,
  type Pagination as PaginationType,
  PersonCreationStatus,
  type PublicOpenSearchResponse,
  v1ProfileInfoNLinkPersonUpdate,
  v1ProfileInfoNUpdateStatusUpdate,
} from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Form } from '@on3/ui-lib/components/Form';
import { Pagination } from '@on3/ui-lib/components/Pagination/Pagination';
import { PlayerTile } from '@on3/ui-lib/components/Player/PlayerTile';
import { externalApi } from '@on3/ui-lib/index';
import { recruitsSubConfig } from '@on3/ui-lib/utils/constants';
import { formatSocialHandle } from '@on3/ui-lib/utils/formatters';
import { yup } from '@on3/ui-lib/utils/yup';
import clsx from 'clsx';
import { useGetRecruitedForm } from 'components/GetRecruited/SearchPage/Form';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import styles from './Results.module.scss';

const ModifySearch = () => {
  const { setStep } = useGetRecruitedForm();

  return (
    <div className={styles.modify}>
      <button name="modify" onClick={() => setStep(0)} type="button">
        <ArrowBack />
        Edit your search
      </button>
    </div>
  );
};

const CreateProfile = () => {
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const { handleSubmit } = useContext(ResultsContext);

  const handleCreateProfile = async (data: LinkPersonToProfileInfoReqBody) => {
    setProcessing(true);

    try {
      await handleSubmit(data);
    } catch (e) {
      setError(
        'An error occurred while creating your profile. Please try again.',
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={styles.createProfile}>
      <button
        disabled={processing}
        name="create-profile"
        onClick={() =>
          handleCreateProfile({
            personKey: null,
          })
        }
        type="button"
      >
        Create a new profile
      </button>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

const ResultsView = ({ pagination }: { pagination?: PaginationType }) => {
  const { athletes, processing, setValues, values } =
    useContext(ResultsContext);

  const handlePersonSelect = useCallback(
    (personKey: number) =>
      setValues((prev) => ({
        personKey: prev.personKey === personKey ? null : personKey,
      })),
    [setValues],
  );

  return (
    <div className={styles.view}>
      <div className={styles.results}>
        {athletes.map((athlete) => (
          <div
            className={clsx(styles.athlete, {
              [styles.selected]: values.personKey === athlete.key,
            })}
            key={athlete.key}
          >
            <button
              className={styles.radio}
              onClick={() => handlePersonSelect(athlete.key)}
              type="button"
            />
            <PlayerTile
              player={{
                ...athlete,
                classYear: athlete.classYear || undefined,
                height: athlete.formattedHeight,
                hometownName: athlete.homeTownName,
                ranking: athlete.rating,
                positionAbbreviation: null,
              }}
              targetType="_blank"
            />
            <div className={styles.status}>
              <span className={styles.position}>
                {athlete.positionAbbreviation}
              </span>
            </div>
          </div>
        ))}
        <Pagination data={pagination} type="expanded" />
        <button
          disabled={!values.personKey || processing}
          name="claim-profile"
          type="submit"
        >
          Claim Profile
        </button>
        <ModifySearch />
      </div>
      <div className={styles.notFound}>
        <h2>Can&apos;t find your profile?</h2>
        <CreateProfile />
      </div>
    </div>
  );
};

const NoResultsView = () => {
  const { info } = useGetRecruitedForm();
  const { processing } = useContext(ResultsContext);

  return (
    <div className={styles.view}>
      <div className={styles.results}>
        <div className={clsx(styles.athlete, styles.selected)}>
          <button className={styles.radio} disabled type="button" />
          <div className={styles.tile}>
            <figure>
              <Avatar />
            </figure>
            <div className={styles.details}>
              <h3>
                {info?.collectedProfileInfo.firstName}{' '}
                {info?.collectedProfileInfo.lastName}
              </h3>
              {info?.collectedProfileInfo.highSchoolClassYear && (
                <dl>
                  <dt>Grad Year:</dt>
                  <dd>{info?.collectedProfileInfo.highSchoolClassYear}</dd>
                </dl>
              )}
              {info?.highSchoolOrganization?.fullName && (
                <dl>
                  <dt>High School:</dt>
                  <dd>{info?.highSchoolOrganization?.fullName}</dd>
                </dl>
              )}
              {info?.collectedProfileInfo.instagram && (
                <dl>
                  <dt>Instagram:</dt>
                  <dd>
                    {formatSocialHandle(info?.collectedProfileInfo.instagram)}
                  </dd>
                </dl>
              )}
            </div>
          </div>
        </div>
        <button disabled={processing} name="claim-profile" type="submit">
          Continue
        </button>
        <ModifySearch />
      </div>
    </div>
  );
};

const ResultsContext = createContext<{
  athletes: PublicOpenSearchResponse[];
  handleSubmit: (data: LinkPersonToProfileInfoReqBody) => Promise<void>;
  processing: boolean;
  setValues: Dispatch<SetStateAction<LinkPersonToProfileInfoReqBody>>;
  values: LinkPersonToProfileInfoReqBody;
}>({} as never);

const schema = yup.object().shape({
  personKey: yup.number().nullable(),
});

export const Results = () => {
  const { setInfo, setStep, info, results } = useGetRecruitedForm();

  const athletes = results?.list || [];

  const [values, setValues] = useState<LinkPersonToProfileInfoReqBody>({
    personKey: athletes.length === 1 ? athletes[0].key : null,
  });

  // If there is no info, reset back to search step
  if (!info) {
    setStep(0);

    return null;
  }

  const handleSubmit = async (data: LinkPersonToProfileInfoReqBody) => {
    try {
      const { key, nonce } = info.collectedProfileInfo;

      const collectedProfileInfo = await v1ProfileInfoNLinkPersonUpdate(
        externalApi,
        key,
        nonce,
        data,
      );

      setInfo(collectedProfileInfo);

      const termId =
        process.env.NEXT_PUBLIC_APP_ENV === 'production'
          ? recruitsSubConfig.piano.production.termId
          : recruitsSubConfig.piano.dev.termId;
      const offerId =
        process.env.NEXT_PUBLIC_APP_ENV === 'production'
          ? recruitsSubConfig.piano.production.offerId
          : recruitsSubConfig.piano.dev.offerId;
      const formId =
        process.env.NEXT_PUBLIC_APP_ENV === 'production'
          ? recruitsSubConfig.piano.production.formId
          : recruitsSubConfig.piano.dev.formId;

      window.tp.offer.startCheckout({
        termId,
        offerId,
        formNameByTermId: JSON.stringify({ [termId]: formId }),
        trackingId: nonce,
      });

      // Update the status to checkout
      // v1ProfileInfoNUpdateStatusUpdate(externalApi, key, nonce, {
      //   status: PersonCreationStatus.Checkout,
      // });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form
      className={styles.form}
      onSubmit={handleSubmit}
      schema={schema}
      values={values}
    >
      {({ processing }) => (
        <ResultsContext.Provider
          value={{
            athletes,
            values,
            setValues,
            handleSubmit,
            processing,
          }}
        >
          {athletes.length > 0 ? (
            <ResultsView pagination={results?.pagination} />
          ) : (
            <NoResultsView />
          )}
        </ResultsContext.Provider>
      )}
    </Form>
  );
};
